import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NbToastrService } from '@nebular/theme';
import { XeroAuthService } from 'src/app/shared/services/xero-auth.service';

interface StateData {
  sessionId: string;
  flowType: 'connect' | 'signin';
  redirectUrl?: string;
}

const ROUTES = {
  CONNECT: '/home/profile/xero',
  SIGNIN: '/home/convert',
  LOGIN: '/auth/login',
} as const;

@Component({
  template: `
    <div class="d-flex justify-content-center align-items-center" style="height: 100vh">
      <nb-card>
        <nb-card-body [nbSpinner]="isLoading">
          <div class="text-center p-4">
            <h3>Processing Xero Connection</h3>
            <p *ngIf="!errorMessage" class="text-hint">Please wait while we complete your connection...</p>

            <div *ngIf="errorMessage" class="mt-4">
              <p class="text-danger mb-3">{{ errorMessage }}</p>

              <button nbButton status="primary" size="medium" (click)="goToLogin()" class="mt-2">
                <nb-icon icon="arrow-back-outline" class="me-2"></nb-icon>
                Back to Login
              </button>
            </div>
          </div>
        </nb-card-body>
      </nb-card>
    </div>
  `,
})
export class XeroCallbackComponent implements OnInit {
  isLoading = true;
  errorMessage = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private xeroService: XeroAuthService,
    private toastrService: NbToastrService,
  ) {}

  async ngOnInit() {
    try {
      const { code, error } = this.route.snapshot.queryParams;

      if (error) {
        throw new Error(`Xero returned an error: ${error}`);
      }

      if (!code) {
        throw new Error('Authorization code not received from Xero');
      }

      const stateData = this.parseStateData();
      await this.processCallback(code, stateData);
      await this.handleSuccess(stateData);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private parseStateData(): StateData {
    const stateParam = this.route.snapshot.queryParams['state'];

    if (!stateParam) {
      throw new Error('State parameter missing from callback');
    }

    try {
      const stateData = JSON.parse(decodeURIComponent(stateParam)) as StateData;

      if (!stateData.sessionId) {
        throw new Error('Session ID missing from state data');
      }

      return stateData;
    } catch {
      throw new Error('Invalid state parameter format');
    }
  }

  private async processCallback(code: string, stateData: StateData): Promise<void> {
    await this.xeroService.handleCallback(code, stateData.sessionId);
  }

  private async handleSuccess(stateData: StateData): Promise<void> {
    const isSignIn = stateData.flowType === 'signin';
    const defaultRoute = isSignIn ? ROUTES.SIGNIN : ROUTES.CONNECT;
    console.log('stateData', stateData);
    console.log('isSignIn', isSignIn);
    console.log('defaultRoute', defaultRoute);

    // Check for redirect URL from session storage (set by auto-redirect route)
    const sessionRedirectUrl = sessionStorage.getItem('xeroAuthRedirectUrl');
    const redirectUrl = stateData.redirectUrl || sessionRedirectUrl || defaultRoute;

    // Clear the session storage redirect URL after use
    if (sessionRedirectUrl) sessionStorage.removeItem('xeroAuthRedirectUrl');

    // Only show toast for connect flow (user is already in app for signin)
    if (!isSignIn) {
      this.toastrService.success('Successfully connected to Xero', 'Success');
    }

    this.router.navigate([redirectUrl]);
  }

  private handleError(error: any): void {
    console.error('Xero callback error:', error);

    this.errorMessage = error?.error?.error || error?.message || 'Failed to complete Xero connection';
    this.isLoading = false;
  }

  goToLogin(): void {
    this.router.navigate([ROUTES.LOGIN]);
  }
}
